// dependencies
import { format, isDate, isValid, parseISO } from "date-fns";
import React from "react";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// components
import { Avatar } from "@performyard/py-components";

import Link from "../../../../components/Link";
import ProgressBar from "../../../../components/ProgressBar";
import ScrollableTable from "../../../../components/TableLite/TableLiteScrollable";
import Typography from "../../../../components/Text";
import TruncatedText from "../../../../components/Text/TruncatedText";
import Tooltip from "../../../../components/Tooltip";

//hooks
import { SearchServiceSort, useCompanySettings } from "../../../../api/hooks";

import { Goal } from "../../../../api/services/searchServiceResultTypes/goal";

import get from "../../../../utils/GetNested";
import parseListToFormattedString from "../../../../utils/ParseListToFormattedString";
// utils
import toEmployeeUrl from "../../../../utils/URLs/Employee";

// constants
import GoalConstants from "../../../../constants/Goals";
// config
import config from "../config";
// styles
import css from "./EmployeeGoalsTable.module.scss";

interface BaseProps {
	goals: Goal[];
	isFetchingGoals: boolean;
	handleColumnHeaderClick: (columnId: SearchServiceSort) => void;
	activeSort: string;
	activeSortDirection: string;
	emptyState: React.ReactNode;
}

const EmployeeGoalsTable = (props: BaseProps) => {
	const { goals, isFetchingGoals, handleColumnHeaderClick, activeSort, activeSortDirection, emptyState } = props;
	const { data: companySettings, isLoading: isCompanySettingsLoading } = useCompanySettings();
	const goalWeightEnabled = companySettings?.functions?.goals?.attributes["goalWeights"] || false;
	const columns = [
		{
			id: "name",
			label: "Goal",
			isSortable: true,
			width: goalWeightEnabled ? 346 : 424,
			getter: (goal) => {
				const goalHref = `/goal/${get(goal, "_id", "")}/`;
				const maxWidth = goalWeightEnabled ? 340 : 416;
				const userCanViewGoal = goal.permissions?.includes("goals:viewDetails") || false;

				return (
					<div className={`${css.goalNameCell} ${css.cell}`}>
						<div data-testid="name" className={`${css.goalName} ${css.clickable}`}>
							{userCanViewGoal ? (
								<Link
									className={`text-truncate ${goal.noTooltip ? "no-goal-tooltip" : ""}`}
									href={goalHref}
								>
									<TruncatedText
										maxWidth={maxWidth}
										size="paragraph-3"
										color="primary-5"
										weight="regular"
										text={get(goal, "name", "")}
										useTooltip="never"
									/>
								</Link>
							) : (
								<TruncatedText
									maxWidth={maxWidth}
									size="paragraph-3"
									weight="regular"
									text={get(goal, "name", "")}
								/>
							)}
						</div>
					</div>
				);
			},
		},
		{
			id: "indicators",
			label: "",
			isSortable: false,
			width: 90,
			getter: (goal) => {
				const indicators = ["transparency", "shared", "unseenUpdates"].map((indicator) => {
					if (indicator === "transparency") {
						const isPrivateGoal = get(goal, "transparency", "") === "private";
						return (
							<div key={indicator} className={css.indicator}>
								<FontAwesomeIcon
									key={indicator}
									className={`${isPrivateGoal ? css.fill : css.opaque} _py-advanced-title`}
									data-tooltip={isPrivateGoal ? "Private Goal" : "Public Goal"}
									data-tipc={
										isPrivateGoal ? "This goal is set to private." : "This goal is set to public."
									}
									icon={["fas", "eye-slash"]}
								/>
							</div>
						);
					}

					if (indicator === "shared") {
						const sharers = get(goal, "sharers", []);
						const isSharedGoal = sharers.length > 0;
						const message = isSharedGoal
							? `This goal is shared with ${parseListToFormattedString(
									sharers.map((sharer) => sharer.name)
								)}`
							: "This goal is not shared.";
						return (
							<div key={indicator} className={css.indicator}>
								<FontAwesomeIcon
									className={`${isSharedGoal ? css.fill : css.opaque} _py-advanced-title`}
									data-tooltip="Shared Goal"
									data-tipc={message}
									icon={["fas", "share-alt"]}
								/>
							</div>
						);
					}

					if (indicator === "unseenUpdates") {
						const hasUnseenUpdates = get(goal, "hasUpdates", false);

						return (
							<div key={indicator} className={css.indicator}>
								<FontAwesomeIcon
									className={`${hasUnseenUpdates ? css.fill : css.opaque} _py-advanced-title`}
									data-tooltip={hasUnseenUpdates ? "New update" : "No new updates"}
									data-tipc={
										hasUnseenUpdates
											? "There has been a new update since you last viewed this goal."
											: "There are no new updates to this goal."
									}
									icon={["fas", "bell"]}
								/>
							</div>
						);
					}

					return null;
				});

				return (
					<div data-testid="indicatorsContainer" className={`${css.indicatorsContainer} ${css.cell}`}>
						{indicators}
					</div>
				);
			},
		},
		{
			id: "assignee.name",
			label: "Assignee",
			isSortable: true,
			width: 159,
			getter: (goal) => {
				const assigneeName = get(goal, ["assignee", "name"], "");
				const assigneeID = get(goal, ["assignee", "_id"], "");
				return (
					<div data-testid="assigneeContainer" className="flex items-center gap-2">
						<Avatar
							size={20}
							dangerouslySetClassName={css.userAvatar}
							id={assigneeID}
							alt={`${assigneeName}'s avatar`}
						/>
						<Link href={toEmployeeUrl(assigneeID)} forceHardReload={true}>
							<TruncatedText
								maxWidth={129}
								size="paragraph-3"
								color="primary-5"
								weight="regular"
								text={assigneeName}
								useTooltip="never"
							/>
						</Link>
					</div>
				);
			},
		},
		{
			id: "end_date",
			label: "Due Date",
			isSortable: true,
			width: 104,
			meta: { dateFormat: config.dateFormat, styles: { width: "1fr" } },
			getter: (goal) => {
				const dueDate = get(goal, "endDate", "");
				const isValidDate = isValid(parseISO(dueDate));
				if (isValidDate) {
					const parsedDate = parseISO(dueDate);
					return (
						<div data-testid="dueDate" className={`${css.goalDueDate} ${css.cell}`}>
							{isDate(parsedDate) && format(parsedDate, "MMM d, yyyy")}
						</div>
					);
				}
			},
		},
		{
			id: "status",
			label: "Status",
			isSortable: true,
			width: 72,
			getter: (goal) => {
				return (
					<div data-testid="status" className={`${css.goalStatus} ${css.cell}`}>
						{get(GoalConstants, ["statusLabels", get(goal, "status")], "")}
					</div>
				);
			},
		},
		{
			id: "progress",
			label: "Progress",
			isSortable: true,
			width: 160,
			getter: (goal) => {
				const progress = get(goal, "progress", 0);
				const color = get(goal, ["outlook", "color"], "neutral-5");
				const needTooltip = progress < -9.99 || progress > 9.99;

				return (
					<div className={css.progress} data-testid="progress">
						<ProgressBar
							progress={progress * 100}
							width={120}
							showAmount={false}
							striped={false}
							animated={false}
							color={color}
						/>
						<Tooltip message={`${progress * 100}%`} disabled={!needTooltip}>
							<Typography element="span" weight="regular" size="paragraph-3">
								<div style={{ color: color }}>
									{needTooltip ? `${progress < 9.99 ? "<-" : ">"}999` : Math.floor(progress * 100)}%
								</div>
							</Typography>
						</Tooltip>
					</div>
				);
			},
		},
	];

	if (goalWeightEnabled) {
		columns.splice(4, 0, {
			id: "goal_weight",
			label: "Weight",
			isSortable: true,
			width: 72,
			getter: (goal) => {
				const goalWeight = goal?.goal_weight;

				return (
					<TruncatedText
						maxWidth={60}
						size="paragraph-3"
						color="neutral-9"
						weight="regular"
						text={String(goalWeight ?? "")}
					/>
				);
			},
		});
	}

	return (
		<div data-testid="goalListContainer">
			<ScrollableTable
				rowKey="_id"
				columns={columns}
				isLoading={isFetchingGoals || isCompanySettingsLoading}
				rows={goals}
				onColumnClick={(columnID, nextDirection) =>
					handleColumnHeaderClick({ name: columnID, direction: nextDirection as "asc" | "desc" })
				}
				activeSort={activeSort}
				activeSortDirection={activeSortDirection}
				emptyChildren={emptyState}
			/>
		</div>
	);
};

export default EmployeeGoalsTable;
